import { z } from 'zod';
import { botScopeSchema, uuidV7Schema } from './ids';
import { timestampSchema } from './assistant';
import { planCodeSchema } from './plans';
import { moneyMinorStringSchema } from './money';

export const billingPolicySchema = z
  .object({
    mode: z.enum(['operator', 'crm']),
    businessConnectionId: uuidV7Schema.nullable(),
    autoRenew: z.boolean(),
    invoiceLeadDays: z.number().int().min(0).max(30),
    nextPlan: planCodeSchema,
  })
  .strict()
  .refine(
    (value) =>
      value.mode === 'crm'
        ? value.businessConnectionId !== null
        : value.businessConnectionId === null,
    'CRM billing requires its own business connection; operator billing has no connection.'
  );
export type BillingPolicy = z.infer<typeof billingPolicySchema>;
export const renewalRequestSchema = z
  .object({ id: uuidV7Schema, startsAt: timestampSchema, plan: planCodeSchema })
  .strict();
// A larger plan for a subscription whose period is still running. Its period
// starts with the request; the server decides that moment.
export const upgradeRequestSchema = z
  .object({ id: uuidV7Schema, plan: planCodeSchema })
  .strict();
export const subscriptionInvoiceSchema = z
  .object({
    scope: botScopeSchema,
    requestId: uuidV7Schema,
    invoiceId: uuidV7Schema,
    invoiceNumber: z.string().min(1),
    plan: planCodeSchema,
    startsAt: timestampSchema,
    endsAt: timestampSchema,
    currency: z.literal('USD'),
    subtotalMinor: moneyMinorStringSchema,
    totalMinor: moneyMinorStringSchema,
    status: z.enum(['pending', 'overdue', 'paid', 'cancelled']),
    paidAt: timestampSchema.nullable(),
    paymentReference: z.string().min(1).nullable(),
  })
  .strict();
export type SubscriptionInvoice = z.infer<typeof subscriptionInvoiceSchema>;
export const operatorPaymentSchema = z
  .object({
    periodId: uuidV7Schema,
    invoiceReference: z.string().trim().min(1).max(500),
    paymentReference: z.string().trim().min(1).max(500),
    paidAt: timestampSchema,
    subtotalMinor: moneyMinorStringSchema,
    currency: z.literal('USD'),
  })
  .strict();
export const billingPeriodSchema = z
  .object({
    id: uuidV7Schema,
    plan: planCodeSchema,
    startsAt: timestampSchema,
    endsAt: timestampSchema,
    mode: z.enum(['operator', 'crm']),
    subtotalMinor: moneyMinorStringSchema,
    state: z.enum(['pending', 'paid', 'cancelled']),
    invoiceReference: z.string().nullable(),
    paymentReference: z.string().nullable(),
    paidAt: timestampSchema.nullable(),
  })
  .strict();
export const billingOverviewSchema = z
  .object({
    policy: billingPolicySchema.nullable(),
    periods: z.array(billingPeriodSchema).max(50),
  })
  .strict();

// Monthly periods preserve the time of day and clamp month-end dates rather than overflowing into a second month.
export function nextSubscriptionPeriodEnd(startsAt: string): string {
  const start = new Date(timestampSchema.parse(startsAt)),
    end = new Date(start);
  const day = start.getUTCDate();
  end.setUTCDate(1);
  end.setUTCMonth(end.getUTCMonth() + 1);
  const last = new Date(
    Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0)
  ).getUTCDate();
  end.setUTCDate(Math.min(day, last));
  return end.toISOString();
}
